import React, { useState, useEffect } from "react";
import axios from "axios";
import { getTimeToken } from "../../utils/timeUtils";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../contexts/AuthContext";

function QA({ socketData }) {
  const [question, setQuestion] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [duration, setDuration] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [startToken, setStartToken] = useState(null);
  const [currentToken, setCurrentToken] = useState(null);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userId, setUserId] = useState(1);
  const [questionId, setQuestionId] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const [isFetchingAnswer, setIsFetchingAnswer] = useState(false); // 👈 این خط رو اضافه کن

  // 🔥 State جدید برای آمار کاربران
  const [userStats, setUserStats] = useState({});

  // 🎯 State جدید برای نتایج کاربر
  const [userResults, setUserResults] = useState([]);
  const [userPoints, setUserPoints] = useState(0);

  const { login, logout, apiBaseUrl } = useAuth();

  const fetchAndLogUserBoard = async (qid) => {
    try {
      const res = await axios.get(
        `${apiBaseUrl}/api/quiz/user/questions/${qid}/results`
      );
      return res.data;
    } catch (err) {
      console.warn(
        "⚠️ userBoard fetch failed:",
        err?.response?.data || err.message
      );
      return null;
    }
  };

  // 🎯 تابع گروه‌بندی کاربران بر اساس answer_id
  const groupUsersByAnswer = (statsData) => {
    if (!statsData || !statsData.data) return {};

    const grouped = {};

    statsData.data.forEach((user) => {
      const answerId = user.data?.answer_id;

      // فقط کاربرانی که جواب دادن رو اضافه کن
      if (answerId !== null && answerId !== undefined) {
        if (!grouped[answerId]) {
          grouped[answerId] = [];
        }
        grouped[answerId].push({
          userId: user.user_id,
          firstName: user.first_name,
          lastName: user.last_name,
          image: user.image,
          isCorrect: user.data.is_correct,
        });
      }
    });

    return grouped;
  };

  useEffect(() => {
    if (socketData && socketData.data && socketData.data.question_id) {
      const newQuestionId = socketData.data.question_id;
      const savedQuestionId = localStorage.getItem("currentQuestionId");

      if (savedQuestionId && parseInt(savedQuestionId) !== newQuestionId) {
        localStorage.removeItem("currentQuestionId");
        localStorage.removeItem("startTime");
        localStorage.removeItem("startToken");
        // Reset کردن آمار برای سوال جدید
        setUserStats({});
        setIsFetchingAnswer(false); // 👈 این خط رو اضافه کن
      }

      setQuestionId(newQuestionId);
    }
  }, [socketData.data.question_id]);

  useEffect(() => {
    if (questionId) {
      const savedQuestionId = localStorage.getItem("currentQuestionId");
      const savedStartTime = localStorage.getItem("startTime");
      const savedStartToken = localStorage.getItem("startToken");

      if (
        savedQuestionId &&
        parseInt(savedQuestionId) === questionId &&
        savedStartTime &&
        savedStartToken
      ) {
        setStartTime(parseInt(savedStartTime));
        setStartToken(savedStartToken);
        fetchQuestionData();
      } else {
        fetchQuestion();
      }
    }
  }, [questionId]);

  const fetchQuestion = async () => {
    try {
      setLoading(true);

      // اول سوال رو بگیر
      const response = await axios.get(
        `${apiBaseUrl}/api/quiz/user/questions/${questionId}`
      );
      const {
        question: questionText,
        answers: options,
        duration: dur,
      } = response.data.data;

      setQuestion(questionText);
      setAnswers(options);
      setDuration(dur);
      setSelectedAnswer(null);
      setCorrectAnswer(null);
      setIsTimeUp(false);
      setHasSubmitted(false);
      setIsFetchingAnswer(false);
      setUserStats({});

      // 👇 اول یه timestamp موقت بذار که timer شروع بشه
      const temporaryStartTime = Math.floor(Date.now() / 1000);
      setStartTime(temporaryStartTime);

      setLoading(false); // 👈 الان سوال نمایش داده میشه و timer شروع میشه

      // 👇 حالا token و timestamp واقعی رو بگیر
      const { token: newStartToken, timestamp: actualStartTime } =
        await getTimeToken();

      // 👇 timestamp واقعی رو جایگزین کن
      setStartToken(newStartToken);
      setStartTime(actualStartTime); // 👈 این replace میشه

      localStorage.setItem("currentQuestionId", questionId);
      localStorage.setItem("startTime", actualStartTime);
      localStorage.setItem("startToken", newStartToken);

      // درخواست برای گرفتن نتایج کاربر
      try {
        const resultsResponse = await axios.get(
          `${apiBaseUrl}/api/quiz/user/questions/results/${socketData.data.challenge_id}`
        );
        if (resultsResponse.data && resultsResponse.data.data) {
          if (resultsResponse.data.data.results) {
            setUserResults(resultsResponse.data.data.results);
          }
          if (resultsResponse.data.data.points !== undefined) {
            setUserPoints(resultsResponse.data.data.points);
          }
        }
      } catch (resultsError) {
        console.error("⚠️ خطا در دریافت results:", resultsError);
      }
    } catch (error) {
      console.error("خطا در دریافت سوال:", error);
      setLoading(false);
    }
  };

  const fetchQuestionData = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${apiBaseUrl}/api/quiz/user/questions/${questionId}`
      );
      const {
        question: questionText,
        answers: options,
        duration: dur,
      } = response.data.data;

      setQuestion(questionText);
      setAnswers(options);
      setDuration(dur);
      setLoading(false);

      // 🔥 درخواست برای گرفتن نتایج کاربر
      try {
        const resultsResponse = await axios.get(
          `${apiBaseUrl}/api/quiz/user/questions/results/${socketData.data.challenge_id}`
        );
        if (resultsResponse.data && resultsResponse.data.data) {
          if (resultsResponse.data.data.results) {
            setUserResults(resultsResponse.data.data.results);
          }
          if (resultsResponse.data.data.points !== undefined) {
            setUserPoints(resultsResponse.data.data.points);
          }
        }
      } catch (resultsError) {
        console.error("⚠️ خطا در دریافت results:", resultsError);
      }
    } catch (error) {
      console.error("خطا در دریافت سوال:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!startTime || !duration) return;

    const calculateTimeLeft = () => {
      const currentTimeInSeconds = Math.floor(new Date().getTime() / 1000);
      const elapsedTime = currentTimeInSeconds - startTime;
      const remainingTime = duration - elapsedTime;
      return remainingTime;
    };

    const initialTime = calculateTimeLeft();
    if (initialTime <= 0) {
      setTimeLeft(0);
      setIsTimeUp(true);
      return;
    }
    setTimeLeft(initialTime);

    const timer = setInterval(() => {
      const remaining = calculateTimeLeft();

      if (remaining <= 0) {
        setTimeLeft(0);
        setIsTimeUp(true);
        clearInterval(timer);
      } else {
        setTimeLeft(remaining);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [startTime, duration]);

  const handleAnswerClick = async (answerId) => {
   if (isTimeUp || isSubmitting) return;

    try {
      setSelectedAnswer(answerId);
      setIsSubmitting(true);

      const { token: newCurrentToken } = await getTimeToken();
      setCurrentToken(newCurrentToken);

      await submitAnswer(answerId, newCurrentToken);
    } catch (error) {
      console.error("خطا در انتخاب پاسخ:", error);
      setIsSubmitting(false);
    }
  };

  const submitAnswer = async (answerId, currentTkn) => {
    try {
      const response = await axios.post(
        `${apiBaseUrl}/api/quiz/user/questions/results`,
        {
          user_id: userId,
          question_id: questionId,
          answer_id: answerId,
          start_time_token: startToken,
          current_time_token: currentTkn,
        }
      );

      setHasSubmitted(true);
      setIsSubmitting(false);
    } catch (error) {
      console.error("خطا در ارسال پاسخ:", error);

      if (error.response && error.response.status === 400) {
        toast.error("زمان پاسخ دادن به سوال تمام شده است!", {
          position: "top-center",
          autoClose: 3000,
          rtl: true,
        });
        setHasSubmitted(false);
      } else {
        toast.error("خطا در ارسال پاسخ. لطفاً دوباره تلاش کنید.", {
          position: "top-center",
          autoClose: 3000,
          rtl: true,
        });
        setHasSubmitted(false);
      }

      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isTimeUp && !correctAnswer) {
      finalizeQuestion();
    }
  }, [isTimeUp]);

  const finalizeQuestion = async () => {
    try {
      const { token: finalCurrentToken } = await getTimeToken();

      const response = await axios.get(
        `${apiBaseUrl}/api/quiz/user/questions/finalize/${questionId}`,
        {
          params: {
            start_time_token: startToken,
            current_time_token: finalCurrentToken,
          },
        }
      );
    } catch (error) {
      console.error("خطا در finalize کردن سوال:", error);
    }
  };

  useEffect(() => {
    if (
      socketData &&
      socketData.data &&
      socketData.data.event === "question_done"
    ) {
      updateCurrentTokenAndFetchAnswer();
    }
  }, [socketData.data.event]);

  const updateCurrentTokenAndFetchAnswer = async () => {
    try {
      const { token: finalCurrentToken } = await getTimeToken();
      setCurrentToken(finalCurrentToken);

      await fetchCorrectAnswer(finalCurrentToken);
    } catch (error) {
      console.error("خطا در به‌روزرسانی توکن:", error);
      setIsSubmitting(false);
    }
  };

  const fetchCorrectAnswer = async (finalToken) => {
    // اگر قبلاً fetch شده، دوباره نکن
    if (isFetchingAnswer || correctAnswer !== null) {
      return;
    }

    setIsFetchingAnswer(true);

    try {
      const response = await axios.get(
        `${apiBaseUrl}/api/quiz/user/questions/final/${questionId}`,
        {
          params: {
            start_time_token: startToken,
            current_time_token: finalToken,
          },
        }
      );

      const correctAnswerObj = response.data.data.answers.find(
        (ans) => ans.is_correct === true
      );

      if (correctAnswerObj) {
        // آمار کاربران رو بگیر
        const statsData = await fetchAndLogUserBoard(questionId);
        let grouped = {};
        if (statsData) {
          grouped = groupUsersByAnswer(statsData);
        }

        // نتایج کاربر رو بگیر
        try {
          const resultsResponse = await axios.get(
            `${apiBaseUrl}/api/quiz/user/questions/results/${socketData.data.challenge_id}`
          );
          if (resultsResponse.data && resultsResponse.data.data) {
            // 🎯 همه رو یکجا set کن
            setCorrectAnswer(correctAnswerObj.id);
            setUserStats(grouped);

            if (resultsResponse.data.data.results) {
              setUserResults(resultsResponse.data.data.results);
            }
            if (resultsResponse.data.data.points !== undefined) {
              setUserPoints(resultsResponse.data.data.points);
            }
          }
        } catch (resultsError) {
          console.error("⚠️ خطا در به‌روزرسانی results:", resultsError);
          // حتی اگر results نیومد، correctAnswer رو set کن
          setCorrectAnswer(correctAnswerObj.id);
          setUserStats(grouped);
        }
      }
    } catch (error) {
      console.error("خطا در دریافت پاسخ صحیح:", error);
    } finally {
      setIsFetchingAnswer(false);
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 h-full flex items-center justify-center">
        <div className="text-center">
          <div
            className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center animate-spin"
            style={{ backgroundColor: "rgba(253, 13, 14, 0.2)" }}
          >
            <div
              className="w-16 h-16 rounded-full"
              style={{ backgroundColor: "#1f2937" }}
            ></div>
          </div>
          <p className="text-gray-300 text-lg">در حال بارگذاری سوال...</p>
        </div>
      </div>
    );
  }

  const isTimeCritical = timeLeft <= 3;

  // 🎯 تابع برای تعیین رنگ دایره بر اساس result
  const getCircleColor = (result) => {
    if (result === true) return "#10b981"; // سبز - درست
    if (result === false) return "#ef4444"; // قرمز - غلط
    return "#6b7280"; // خاکستری - جواب نداده
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={questionId}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.4 }}
        className="p-6 h-full flex flex-col"
      >
        {/* Header - فشرده‌تر */}
        <div className="mb-6 flex justify-between items-center gap-6">
          {/* Timer */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="timer-card rounded-xl px-6 py-3 border backdrop-blur-sm shadow-xl flex items-center gap-3 relative overflow-hidden"
            style={{
              backgroundColor: "rgba(31, 41, 55, 0.8)",
              borderColor: isTimeCritical
                ? "rgba(239, 68, 68, 0.5)"
                : "rgba(16, 185, 129, 0.4)",
            }}
          >
            <div
              className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all duration-300 relative z-10`}
              style={{
                backgroundColor: isTimeCritical ? "#ef4444" : "#10b981",
                boxShadow: isTimeCritical
                  ? "0 0 20px rgba(239, 68, 68, 0.5)"
                  : "0 0 15px rgba(16, 185, 129, 0.4)",
              }}
            >
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="relative z-10">
              <p className="text-xs text-gray-400 font-medium">زمان</p>
              <p
                className={`text-2xl font-bold transition-colors duration-300 ${
                  isTimeCritical ? "text-red-400" : "text-green-400"
                }`}
              >
                {timeLeft}s
              </p>
            </div>
          </motion.div>

          {/* Points */}
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="points-card rounded-xl px-5 py-3 border backdrop-blur-sm shadow-xl flex items-center gap-3 relative overflow-hidden"
            style={{
              backgroundColor: "rgba(31, 41, 55, 1)",
              borderColor: "rgba(16, 185, 129, 0.4)",
            }}
          >
            <div className="points-glow"></div>
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 relative z-10"
              style={{
                backgroundColor: "#10b981",
                boxShadow: "0 0 20px rgba(16, 185, 129, 0.5)",
              }}
            >
              <svg
                className="w-5 h-5 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </div>
            <div className="relative z-10">
              <p className="text-xs text-gray-400 font-medium">امتیاز</p>
              <p className="text-xl font-bold text-green-400">{userPoints}</p>
            </div>
          </motion.div>
        </div>

        {/* Question - فشرده‌تر */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="mb-6"
        >
          <div
            className="question-card rounded-xl p-5 border backdrop-blur-sm shadow-xl relative overflow-hidden"
            style={{
              backgroundColor: "rgba(31, 41, 55, 0.8)",
              borderColor: "rgba(253, 13, 14, 0.3)",
            }}
          >
            <div className="question-shimmer"></div>
            <div className="flex items-start gap-4 relative z-10">
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: "#fd0d0e",
                  boxShadow: "0 4px 15px rgba(253, 13, 14, 0.4)",
                }}
              >
                <span className="text-white text-2xl font-bold">؟</span>
              </div>
              <div className="flex-1">
                <p className="text-lg text-white font-medium leading-relaxed">
                  {question}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Answers - فشرده‌تر */}
        <div className="flex-1 flex items-center justify-center mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl w-full">
            {answers.map((answer, index) => {
              const isSelected = selectedAnswer === answer.id;
              const isCorrect = correctAnswer === answer.id;
              const showResult = isTimeUp && correctAnswer;
              const usersWhoSelected = userStats[answer.id] || [];

              return (
<motion.button
  key={answer.id}
  initial={{ opacity: 0, scale: 0.9 }}
  animate={{ opacity: 1, scale: 1 }}
  transition={{ delay: 0.4 + index * 0.1, duration: 0.4 }}
  whileHover={!isTimeUp && !isSubmitting ? { scale: 1.02, y: -2 } : {}}
  whileTap={!isTimeUp && !isSubmitting ? { scale: 0.98 } : {}}
  onClick={() => handleAnswerClick(answer.id)}
  disabled={isTimeUp || isSubmitting} // 👈 فقط این دو شرط
  className={`answer-button rounded-lg p-4 border transition-all duration-300 text-right relative overflow-hidden ${
    isTimeUp || isSubmitting // 👈 فقط این دو شرط
      ? "cursor-not-allowed opacity-70"
      : "cursor-pointer"
  } ${
    showResult && isCorrect
      ? "border-green-500"
      : showResult && isSelected && !isCorrect
      ? "border-red-500"
      : isSelected
      ? "border-yellow-500"
      : "border-gray-600"
  }`}
  style={{
    backgroundColor:
      showResult && isCorrect
        ? "rgba(6, 95, 70, 0.6)"
        : showResult && isSelected && !isCorrect
        ? "rgba(127, 29, 29, 0.6)"
        : isSelected
        ? "rgba(113, 63, 18, 0.5)"
        : "rgba(17, 24, 39, 0.6)",
    boxShadow:
      showResult && isCorrect
        ? "0 4px 20px rgba(16, 185, 129, 0.3)"
      : showResult && isSelected && !isCorrect
        ? "0 4px 20px rgba(239, 68, 68, 0.3)"
        : isSelected
        ? "0 4px 20px rgba(234, 179, 8, 0.3)"
        : "none",
  }}
>
                  {isSelected && !showResult && (
                    <div className="answer-selected-glow-yellow"></div>
                  )}

                  <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-2">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{
                          backgroundColor:
                            showResult && isCorrect
                              ? "#10b981"
                              : showResult && isSelected && !isCorrect
                              ? "#ef4444"
                              : isSelected
                              ? "#eab308"
                              : "rgba(55, 65, 81, 0.8)",
                          boxShadow:
                            showResult && isCorrect
                              ? "0 0 15px rgba(16, 185, 129, 0.5)"
                              : showResult && isSelected && !isCorrect
                              ? "0 0 15px rgba(239, 68, 68, 0.5)"
                              : isSelected
                              ? "0 0 15px rgba(234, 179, 8, 0.5)"
                              : "none",
                        }}
                      >
                        {showResult && isCorrect ? (
                          <svg
                            className="w-5 h-5 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        ) : showResult && isSelected && !isCorrect ? (
                          <svg
                            className="w-5 h-5 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        ) : (
                          <span className="text-white font-bold text-base">
                            {["الف", "ب", "ج", "د"][index]}
                          </span>
                        )}
                      </div>
                      <p
                        className={`text-base font-medium leading-relaxed transition-colors duration-300 ${
                          isSelected || (showResult && isCorrect)
                            ? "text-white"
                            : "text-gray-300"
                        }`}
                      >
                        {answer.answer}
                      </p>
                    </div>

                    {showResult && usersWhoSelected.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        transition={{ delay: 0.3, duration: 0.4 }}
                        className="mt-2 pt-2 border-t border-gray-600/50"
                      >
                        <div className="flex items-center gap-1 flex-wrap">
                          {usersWhoSelected.slice(0, 8).map((user, idx) => (
                            <motion.div
                              key={user.userId}
                              initial={{ opacity: 0, scale: 0.5 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: 0.4 + idx * 0.03 }}
                              className="relative group"
                            >
                              <div
                                className="w-12 h-12 rounded-full border-2 overflow-hidden flex items-center justify-center hover:scale-110 transition-transform cursor-pointer"
                                style={{
                                  marginLeft: idx > 0 ? "-12px" : "-12px",
                                  backgroundColor: "white",
                                  borderColor: "#10b981",
                                  boxShadow: "0 0 8px rgba(16, 185, 129, 0.4)",
                                }}
                              >
                                <img
                                  src="https://i.pravatar.cc/150?img=1"
                                  // src={user.image}
                                  alt={`${user.firstName}`}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                                {user.firstName} {user.lastName}
                              </div>
                            </motion.div>
                          ))}
                          {usersWhoSelected.length > 8 && (
                            <div
                              className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-xs text-white font-bold"
                              style={{ marginLeft: "-6px" }}
                            >
                              +{usersWhoSelected.length - 8}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Result + Performance - کنار هم */}
        <div className="flex gap-4 justify-center items-center">
          {isTimeUp && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="rounded-xl px-6 py-2 border backdrop-blur-sm shadow-lg"
              style={{
                backgroundColor: "rgba(31, 41, 55, 0.8)",
                borderColor: !hasSubmitted
                  ? "rgba(107, 114, 128, 0.5)"
                  : selectedAnswer && correctAnswer === selectedAnswer
                  ? "rgba(16, 185, 129, 0.5)"
                  : "rgba(239, 68, 68, 0.5)",
              }}
            >
              <p className="text-white text-center text-sm font-medium">
                {!hasSubmitted
                  ? "زمان تمام شد"
                  : correctAnswer === null // 👈 اول چک کن correctAnswer اومده یا نه
                  ? "در حال بررسی..." // 👈 اگه نیومده، این رو نشون بده
                  : selectedAnswer === correctAnswer
                  ? "پاسخ صحیح!"
                  : "پاسخ اشتباه"}
              </p>
            </motion.div>
          )}

          {/* {userResults.length > 0 && (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3, duration: 0.4 }}
        className="rounded-xl px-5 py-2 border backdrop-blur-sm shadow-lg"
        style={{ 
          backgroundColor: 'rgba(31, 41, 55, 0.9)',
          borderColor: 'rgba(75, 85, 99, 0.3)'
        }}
      >
        <div className="flex items-center gap-2">
          {userResults.map((item, index) => (
            <motion.div
              key={item.question_id}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 + index * 0.04 }}
              className="relative group"
            >
              <div
                className={`w-8 h-8 rounded-lg transition-all duration-300 hover:scale-110 cursor-pointer flex items-center justify-center ${
                  item.question_id === questionId ? 'ring-2 ring-white' : ''
                }`}
                style={{
                  backgroundColor: getCircleColor(item.result),
                  boxShadow: `0 2px 10px ${getCircleColor(item.result)}40`,
                }}
              >
                {item.result === true && (
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                {item.result === false && (
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
                {item.result === null && (
                  <span className="text-white font-bold text-xs">؟</span>
                )}
              </div>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                <div className="bg-gray-900 text-white text-xs rounded-lg shadow-xl p-2 whitespace-nowrap border border-gray-700">
                  <div className="font-semibold mb-1">{item.question}</div>
                  <div className="text-gray-300 text-xs">
                    {item.result === true ? '✓ صحیح' : item.result === false ? '✗ غلط' : '○ بدون پاسخ'}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    )} */}

          {userResults.length > 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              className="rounded-xl px-5 py-2 border backdrop-blur-sm shadow-lg"
              style={{
                backgroundColor: "rgba(31, 41, 55, 0.9)",
                borderColor: "rgba(75, 85, 99, 0.3)",
              }}
            >
              <div className="flex items-center gap-2">
                {userResults.map((item, index) => (
                  <motion.div
                    key={item.question_id}
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 + index * 0.04 }}
                    className="relative group"
                  >
                    <div
                      className={`w-8 h-8 rounded-lg transition-all duration-300 hover:scale-110 cursor-pointer flex flex-col items-center justify-center ${
                        item.question_id === questionId
                          ? "ring-2 ring-white"
                          : ""
                      }`}
                      style={{
                        backgroundColor: getCircleColor(item.result),
                        boxShadow: `0 2px 10px ${getCircleColor(
                          item.result
                        )}40`,
                      }}
                    >
                      {/* شماره سوال */}
                      <span className="text-white font-bold text-[10px] leading-tight">
                        {index + 1}
                      </span>

                      {/* آیکون نتیجه */}
                      {item.result === true && (
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                      {item.result === false && (
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      )}
                      {item.result === null && (
                        <span className="text-white font-bold text-[8px]">
                          ؟
                        </span>
                      )}
                    </div>

                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                      <div className="bg-gray-900 text-white text-xs rounded-lg shadow-xl p-2 whitespace-nowrap border border-gray-700">
                        {/* <div className="font-semibold mb-1">
                          سوال {index + 1}: {item.question}
                        </div> */}
                        <div className="text-gray-300 text-xs">
                          {item.result === true
                            ? "✓ صحیح"
                            : item.result === false
                            ? "✗ غلط"
                            : "○ بدون پاسخ"}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>

      <style>{`
        @keyframes timer-pulse-anim {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 30px rgba(239, 68, 68, 0.6);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 0 40px rgba(239, 68, 68, 0.8);
          }
        }

        .timer-pulse {
          animation: timer-pulse-anim 1s ease-in-out infinite;
        }

        @keyframes shimmer-anim {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        .question-shimmer {
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
          animation: shimmer-anim 3s infinite;
        }

        @keyframes glow-pulse {
          0%, 100% {
            opacity: 0.4;
          }
          50% {
            opacity: 0.7;
          }
        }

        .timer-glow-red {
          position: absolute;
          inset: -2px;
          background: linear-gradient(135deg, #ef4444, #dc2626);
          border-radius: 16px;
          filter: blur(15px);
          z-index: 0;
          animation: glow-pulse 2s ease-in-out infinite;
        }

        .timer-glow-green {
          position: absolute;
          inset: -2px;
          background: linear-gradient(135deg, #10b981, #059669);
          border-radius: 16px;
          filter: blur(15px);
          z-index: 0;
          animation: glow-pulse 2s ease-in-out infinite;
        }

      .points-glow {
  position: absolute;
  inset: -2px;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 16px;
  filter: blur(15px);
  z-index: 0;
  animation: glow-pulse 2s ease-in-out infinite;
}

        .answer-selected-glow-yellow {
          position: absolute;
          inset: -2px;
          background: linear-gradient(135deg, #eab308, #ca8a04);
          border-radius: 12px;
          filter: blur(20px);
          z-index: 0;
          animation: glow-pulse 1.5s ease-in-out infinite;
        }

        .answer-button {
          backdrop-filter: blur(10px);
        }

        .timer-card, .question-card {
          backdrop-filter: blur(10px);
        }

        .points-card {
          backdrop-filter: blur(10px);
        }

        .result-shimmer {
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(253, 13, 14, 0.1), transparent);
          animation: shimmer-anim 3s infinite;
        }
      `}</style>
    </AnimatePresence>
  );
}

export default QA;
